/**
 * 重跑结果槽位复用
 *
 * generate 节点重新生成时，结果卡片（自动生成子节点）优先**原地复用**而非删了重建：
 * 复用同一节点 ID → 用户拖过的位置、挂在卡片上的手工连线（如 图片 → 生视频）全部保留，
 * 视觉上是「卡片清空重新生成」而不是「多出一张新卡片」。
 *
 * 只有结构变更（结果类型变化，如 单图 ↔ 图片组）才删除旧卡片重建——此时节点身份
 * 无法延续，手工连线随节点一并消失。
 */
import { boardActions, getBoardState } from '../state/boardStore';
import { nid } from '../utils';
import type { CanvasNode } from '../types';

export type ResultSlotKind = 'image' | 'image_group' | 'video' | 'video_group';

interface ReuseAutoChildSlotOptions {
  /** 无可复用子节点时新建（调用方负责初始数据与 _autoGenerated 标记） */
  create: () => CanvasNode;
  /** 复用已有子节点前清空其数据（重置为生成中） */
  reset: (nodeId: string) => void;
}

/**
 * 取得本轮生成的结果节点 ID：
 * 已有同类型自动生成子节点 → 重置复用；类型不匹配或多于 1 个 → 删除；没有 → 新建并连线。
 */
export function reuseAutoChildSlot(
  generateNode: CanvasNode,
  kind: ResultSlotKind,
  options: ReuseAutoChildSlotOptions
): string {
  const state = getBoardState();
  const childIds = new Set(
    state.edges
      .filter((e) => e.sourceNodeId === generateNode.id && e.sourceHandle === 'right')
      .map((e) => e.targetNodeId)
  );
  const autoChildren = state.nodes.filter(
    (n) => childIds.has(n.id) && (n.data as Record<string, unknown>)._autoGenerated === true
  );
  const reusable = autoChildren.filter((n) => n.type === kind);
  const obsoleteIds = [
    ...autoChildren.filter((n) => n.type !== kind),
    ...reusable.slice(1),
  ].map((n) => n.id);
  if (obsoleteIds.length > 0) {
    boardActions.removeNodes(obsoleteIds);
  }

  if (reusable.length > 0) {
    options.reset(reusable[0].id);
    return reusable[0].id;
  }

  const node = options.create();
  boardActions.addNodes([node], [{
    id: nid(),
    sourceNodeId: generateNode.id,
    targetNodeId: node.id,
    sourceHandle: 'right',
    targetHandle: 'left',
  }]);
  return node.id;
}
